import { useEffect, useRef, useState } from 'react';

/**
 * Subscribe to real-time backend events via WebSocket.
 * Auto-reconnects with exponential backoff.
 *
 * StrictMode-safe: the first connection is deferred via setTimeout(0).
 * React 18 StrictMode mounts → unmounts → remounts effects in dev. The
 * deferred timer is cancelled by the transient unmount, so we only ever
 * open ONE socket per real mount, avoiding duplicate events.
 *
 * Usage:
 *   useDashboardWebSocket((event, data) => { ... });
 */
const useDashboardWebSocket = (onEvent) => {
  const [connected, setConnected] = useState(false);
  const onEventRef = useRef(onEvent);

  // Keep latest handler reference without re-running the connect effect.
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) return undefined;
    const wsUrl = backendUrl.replace(/^http/, 'ws') + '/api/ws';

    let ws = null;
    let reconnectTimer = null;
    let heartbeatTimer = null;
    let initialTimer = null;
    let attempt = 0;
    let cancelled = false;

    const scheduleReconnect = () => {
      if (cancelled || reconnectTimer) return;
      attempt += 1;
      const delay = Math.min(1000 * 2 ** attempt, 30000); // cap at 30s
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (cancelled) return;
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        scheduleReconnect();
        return;
      }

      ws.onopen = () => {
        if (cancelled) { try { ws.close(); } catch { /* ignore */ } return; }
        attempt = 0;
        setConnected(true);
      };

      ws.onmessage = (evt) => {
        if (cancelled) return;
        try {
          const msg = JSON.parse(evt.data);
          if (msg && msg.event && onEventRef.current) {
            onEventRef.current(msg.event, msg.data || {});
          }
        } catch {
          /* ignore non-JSON */
        }
      };

      ws.onerror = () => { /* swallow — onclose follows */ };

      ws.onclose = () => {
        setConnected(false);
        if (!cancelled) scheduleReconnect();
      };
    };

    // Defer initial connect so StrictMode's transient mount→unmount cycle
    // resolves before any socket is actually opened.
    initialTimer = setTimeout(connect, 0);

    // Lightweight heartbeat (every 25s) so proxies don't kill the connection
    heartbeatTimer = setInterval(() => {
      if (ws && ws.readyState === 1) {
        try { ws.send('ping'); } catch { /* ignore */ }
      }
    }, 25000);

    return () => {
      cancelled = true;
      if (initialTimer) clearTimeout(initialTimer);
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      if (ws) {
        // Detach handlers BEFORE close so the trailing onclose doesn't
        // trigger another reconnect attempt.
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        try { ws.close(); } catch { /* ignore */ }
        ws = null;
      }
    };
  }, []);

  return { connected };
};

export default useDashboardWebSocket;
